/**
 * @fileoverview Own document upload invitation controls for the selected matter client. Payment requests and verification belong exclusively to `PaymentInstructionsHistory.js`. Each invitation retains its own link and feedback; backend context owns creation eligibility. Creating or copying a link does not send a request or verify the client.
 */

import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import { AppIcon } from '../shared';
import './PortalInvitations.css';

function PortalFeatureRow({ title, status, actions, children }) {
  return <section className="portal-feature-row" aria-label={title}>
    <div className="portal-feature-row__summary">
      <h3>{title}</h3>
      {status && <div className="portal-feature-row__status">{status}</div>}
    </div>
    <div className="portal-feature-row__actions">{actions}</div>
    {children && <div className="portal-feature-row__feedback">{children}</div>}
  </section>;
}

export default function PortalInvitations(props) {
  return <ScopedPortalInvitations key={`${props.matterNumber}:${props.clientId}`} {...props} />;
}

function ScopedPortalInvitations({ matterNumber, clientId, clientName }) {
  const [context, setContext] = useState(null);
  const [busy, setBusy] = useState('');
  const [invitations, setInvitations] = useState({});
  const active = useRef(true);
  const copyTimers = useRef({});

  useEffect(() => {
    active.current = true;
    api.get(`/matters/${encodeURIComponent(matterNumber)}/portal/context`).then(({ data }) => {
      if (active.current) setContext(data);
    }).catch(() => { if (active.current) setContext(null); });
    const timers = copyTimers.current;
    return () => {
      active.current = false;
      Object.values(timers).forEach(clearTimeout);
    };
  }, [matterNumber]);

  const enabled = context?.enabled && context.clients?.some(client => String(client.id) === String(clientId));

  function update(kind, values) {
    if (active.current) setInvitations(current => ({ ...current, [kind]: { ...current[kind], ...values } }));
  }

  async function copyLink(kind, url) {
    clearTimeout(copyTimers.current[kind]);
    try {
      await navigator.clipboard.writeText(url);
      if (!active.current) return;
      update(kind, { copyStatus: 'copied' });
      copyTimers.current[kind] = setTimeout(() => update(kind, { copyStatus: '' }), 2500);
    } catch {
      update(kind, { copyStatus: 'blocked' });
    }
  }

  async function create(kind) {
    setBusy(kind);
    update(kind, { error: '' });
    try {
      const { data } = await api.post(`/matters/${encodeURIComponent(matterNumber)}/portal/${kind}-invitations`, {
        client_id: Number(clientId),
      });
      if (!active.current) return;
      update(kind, { url: data.url, expiresAt: data.expires_at, copyStatus: '' });
      await copyLink(kind, data.url);
    } catch (error) {
      update(kind, { error: error.response?.data?.error || 'The invitation could not be created. Please retry.' });
    } finally {
      if (active.current) setBusy('');
    }
  }

  function feature() {
    const kind = 'upload';
    const review = {};
    const title = 'Document uploads';
    const name = 'Upload';
    const { url, copyStatus, expiresAt, error } = invitations[kind] || {};
    const canCreate = enabled;
    if (!canCreate && !review.canOpen) return null;
    const createLabel = 'Create upload link';
    const createButton = <button type="button" className="portal-invitations__button" aria-label={createLabel} disabled={!!busy || !clientId} onClick={event => {
      const menu = event.currentTarget.closest('details');
      if (menu) menu.open = false;
      create(kind);
    }}>
      <AppIcon icon={busy === kind ? 'pi pi-spin pi-spinner' : 'pi pi-plus'} aria-hidden="true" />Create link
    </button>;
    const linkId = `portal-link-${matterNumber}-${clientId}-${kind}`;
    return <PortalFeatureRow title={title} status={review.status || (url ? 'Link ready' : null)} actions={<>
      {url && <button type="button" className="portal-invitations__text-button" aria-label={`Copy ${name.toLowerCase()} link`} onClick={() => copyLink(kind, url)}>
        <AppIcon icon={copyStatus === 'copied' ? 'pi pi-check' : 'pi pi-copy'} aria-hidden="true" />{copyStatus === 'copied' ? 'Copied' : 'Copy link'}
      </button>}
      {review.action || (!url && canCreate ? createButton : null)}
      {(url || (review.action && canCreate)) && <details className="portal-invitations__options">
        <summary aria-label={`${title} options`}><AppIcon icon="pi pi-ellipsis-h" aria-hidden="true" /></summary>
        <div className="portal-invitations__menu">
          {expiresAt && <span className="portal-invitations__expiry">Expires {new Date(expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>}
          {url && <a href={url} target="_blank" rel="noreferrer" aria-label={`Open ${name.toLowerCase()} invitation`}>Open client link<AppIcon icon="pi pi-external-link" aria-hidden="true" /></a>}
          {canCreate && createButton}
        </div>
      </details>}
    </>}>
      {copyStatus === 'copied' && <span role="status" className="portal-invitations__copy-notice">{name} link copied</span>}
      {copyStatus === 'blocked' && <div className="portal-invitations__copy-fallback">
        <label htmlFor={linkId}>Copying was blocked. Select the link to copy it.</label>
        <input id={linkId} aria-label={`${name} invitation link`} readOnly value={url} onFocus={event => event.target.select()} />
      </div>}
      {error && <p role="alert">{error}</p>}
    </PortalFeatureRow>;
  }

  return <div className="portal-invitations" aria-label={`Portal actions for ${clientName}`}>
    {feature()}
  </div>;
}
